import { linearPriceRequestMapper } from '@domains/subscription/priv/Billables/usecases/Price/linear/linearMapper'
import { MaxPriceRequestMapper } from '@domains/subscription/priv/Billables/usecases/Price/max/maxMapper'
import { MinPriceRequestMapper } from '@domains/subscription/priv/Billables/usecases/Price/min/minMapper'
import { TieredPriceRequestMapper } from '@domains/subscription/priv/Billables/usecases/Price/tiered/tieredMapper'
import {
  AnchorablePeriod,
  type AnchoredToSignup,
  type AssignedPriceAlgorithm,
  type DayOfWeek,
  type Fixed,
  type TemplateAssignedPrice,
  type TemplateAssignedPriceFrequency,
  type TemplatePriceRequest,
} from '@mynt/backoffice-api-spec/billables'
import {
  type AnchorablePayment,
  type BillablePrice,
  type CreatePriceRequest,
  type FixedPayment,
  type PaymentFrequencyRequest,
  WeekDay,
} from './api'

export interface PriceRequestMapper {
  toPriceRequestDto: (price: CreatePriceRequest, mapper: (price: CreatePriceRequest) => PriceRequestMapper) => TemplatePriceRequest
  toAssignedPriceAlgorithm: (price: CreatePriceRequest, mapper: (price: CreatePriceRequest) => PriceRequestMapper) => AssignedPriceAlgorithm
  supports: (price: CreatePriceRequest) => boolean
}

export interface AssignedPriceMapper {
  toBillablePrice: (assignedPrice: TemplateAssignedPrice) => BillablePrice
  supports: (assignedPrice: TemplateAssignedPrice) => boolean
}

const getCreatePriceRequestToPriceRequestDtoMapper = (price: CreatePriceRequest): PriceRequestMapper => {
  const mappers: PriceRequestMapper[] = [
    new linearPriceRequestMapper(),
    new TieredPriceRequestMapper(),
    new MaxPriceRequestMapper(),
    new MinPriceRequestMapper(),
  ]

  for (const mapper of mappers) {
    if (mapper.supports(price)) {
      return mapper
    }
  }

  throw new Error(`No suitable mapper found for price request type: ${price.type}`)
}

export const mapCreatePriceRequestToPriceRequestDto = (price: CreatePriceRequest): TemplatePriceRequest => {
  const mapper = getCreatePriceRequestToPriceRequestDtoMapper(price)
  return mapper.toPriceRequestDto(price, getCreatePriceRequestToPriceRequestDtoMapper)
}

export const mapAssignedPaymentFrequency = (frequency: TemplateAssignedPriceFrequency): PaymentFrequencyRequest => {
  switch (frequency.type) {
    case 'AnchoredToSignup': {
      return {
        type: 'anchorable',
        period: mapAnchorablePeriodToPeriod(frequency.anchorablePeriod),
      }
    }

    case 'Fixed': {
      return mapPaymentFrequencyToFixedPayment(frequency.paymentFrequency)
    }
  }
}

export const mapPaymentFrequencyToPriceFrequency = (payment: PaymentFrequencyRequest): TemplateAssignedPriceFrequency => {
  switch (payment.type) {
    case 'anchorable': {
      return {
        type: 'AnchoredToSignup',
        anchorablePeriod: mapPeriodToAnchorablePeriod(payment.period),
      }
    }

    case 'fixed': {
      return {
        type: 'Fixed',
        paymentFrequency: mapFixedPaymentFrequency(payment),
      }
    }
  }
}

export const mapAnchorablePaymentFrequency = (payment: AnchorablePayment): AnchoredToSignup => {
  return {
    type: 'AnchoredToSignup',
    anchorablePeriod: mapPeriodToAnchorablePeriod(payment.period),
  }
}

export const mapFixedPaymentFrequency = (payment: FixedPayment): Fixed['paymentFrequency'] => {
  switch (payment.frequency) {
    case 'daily': {
      return {
        type: 'Daily',
      }
    }

    case 'weekly': {
      return {
        type: 'WeeklyOn',
        dayOfWeek: mapWeekDayToDayOfWeek(payment.dayOfWeek),
      }
    }

    case 'monthly': {
      return {
        type: 'MonthlyOn',
        day: payment.dayOfMonth,
      }
    }

    case 'yearly': {
      return {
        type: 'YearlyOn',
        day: payment.dayOfYear,
      }
    }
  }
}

export const mapPaymentFrequencyToFixedPayment = (payment: Fixed['paymentFrequency']): FixedPayment => {
  switch (payment.type) {
    case 'Daily': {
      return {
        type: 'fixed',
        frequency: 'daily',
      }
    }

    case 'WeeklyOn': {
      return {
        type: 'fixed',
        frequency: 'weekly',
        dayOfWeek: mapDayOfWeekToWeekDay(payment.dayOfWeek),
      }
    }

    case 'MonthlyOn': {
      return {
        type: 'fixed',
        frequency: 'monthly',
        dayOfMonth: payment.day,
      }
    }

    case 'YearlyOn': {
      return {
        type: 'fixed',
        frequency: 'yearly',
        dayOfYear: payment.day,
      }
    }
  }
}

const mapDayOfWeekToWeekDay = (dayOfWeek: DayOfWeek): WeekDay => {
  switch (dayOfWeek) {
    case 'MONDAY':
      return WeekDay.Monday
    case 'TUESDAY':
      return WeekDay.Tuesday
    case 'WEDNESDAY':
      return WeekDay.Wednesday
    case 'THURSDAY':
      return WeekDay.Thursday
    case 'FRIDAY':
      return WeekDay.Friday
    case 'SATURDAY':
      return WeekDay.Saturday
    case 'SUNDAY':
      return WeekDay.Sunday
  }
}

const mapWeekDayToDayOfWeek = (day: WeekDay): DayOfWeek => {
  switch (day) {
    case WeekDay.Monday:
      return 'MONDAY'
    case WeekDay.Tuesday:
      return 'TUESDAY'
    case WeekDay.Wednesday:
      return 'WEDNESDAY'
    case WeekDay.Thursday:
      return 'THURSDAY'
    case WeekDay.Friday:
      return 'FRIDAY'
    case WeekDay.Saturday:
      return 'SATURDAY'
    case WeekDay.Sunday:
      return 'SUNDAY'
  }
}

const mapPeriodToAnchorablePeriod = (period: AnchorablePayment['period']): AnchorablePeriod => {
  switch (period) {
    case 'weekly':
      return AnchorablePeriod.WEEKLY

    case 'monthly':
      return AnchorablePeriod.MONTHLY

    case 'yearly':
      return AnchorablePeriod.YEARLY
  }
}

const mapAnchorablePeriodToPeriod = (period: AnchorablePeriod): AnchorablePayment['period'] => {
  switch (period) {
    case AnchorablePeriod.WEEKLY:
      return 'weekly'
    case AnchorablePeriod.MONTHLY:
      return 'monthly'
    case AnchorablePeriod.YEARLY:
      return 'yearly'
  }
}

export const mapTemplateAssignedPriceToHasAnchoredToSignupPrice = (price: TemplateAssignedPrice): boolean => {
  return price.priceFrequency.type === 'AnchoredToSignup'
}
